import { useQueryClient } from '@tanstack/react-query';
import useApi from './useApi';

/**
 * Custom hook that schedules delayed query invalidations based on a refresh configuration.
 * Dynamically calculates delay based on record count to optimize refresh timing.
 * 
 * @returns {Function} refreshLater function
 */
export function useRefreshLater() {
    const queryClient = useQueryClient();
    const { getMe } = useApi();

    /**
     * Schedules query invalidations with dynamic timing based on record count
     * 
     * @param {Object} refresh - Refresh config object
     * @param {string} id - ID to substitute in path template
     * @param {string} action - Action name to match in refresh config
     */
    return async function refreshLater(refresh, id, action) {
        // Early return if no refresh config or action
        if (!refresh?.[action]) return;

        const [pathTemplate, fallbackDelay, resourceToCount] = refresh[action];
        const resolvedPath = pathTemplate.replace(':id', id);
        
        let totalTime = fallbackDelay;

        // Calculate dynamic delay based on record count if resource is specified
        if (resourceToCount) {
            try {
                const countResponse = await getMe(`${resourceToCount}/count`);
                const recordCount = extractCountFromResponse(countResponse);
                
                if (recordCount > 0) {
                    totalTime = Math.max(recordCount * 200, 2000); // Minimum 2 seconds
                }
            } catch (error) {
                console.warn(`Failed to get count for ${resourceToCount}, using fallback delay:`, error);
            }
        }

        // Schedule invalidations in chunks to avoid overwhelming the system
        scheduleInvalidations(queryClient, resolvedPath, totalTime);
    };
}

/**
 * Extracts count value from API response, handling different response formats
 * 
 * @param {*} response - API response
 * @returns {number} - Extracted count value
 */
function extractCountFromResponse(response) {
    if (typeof response === 'number') return response;
    if (typeof response === 'object' && response?.count !== undefined) return response.count;
    return 0;
}

/**
 * Schedules query invalidations in chunks to prevent system overload
 * 
 * @param {Object} queryClient - React Query client
 * @param {string} pathPattern - Path pattern to match queries
 * @param {number} totalTime - Total time to spread invalidations over
 */
function scheduleInvalidations(queryClient, pathPattern, totalTime) {
    const CHUNK_SIZE = 2000; // 2 second chunks
    const chunks = Math.ceil(totalTime / CHUNK_SIZE);
    
    for (let i = 1; i <= chunks; i++) {
        const delay = i * CHUNK_SIZE;
        
        setTimeout(() => {
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const queryKey = query.queryKey?.[0];
                    return typeof queryKey === 'string' && queryKey.startsWith(pathPattern);
                }
            });
        }, delay);
    }
}
